/**
 * Lazy-loads `modern-screenshot` on first use. ~10 KB gzipped, fetched only
 * when the user actually clicks "Add screenshot" — then cached by the browser
 * for the rest of the session.
 *
 * Why modern-screenshot instead of html2canvas:
 *  - Handles modern CSS color functions (`lab()`, `oklch()`, `color-mix()`)
 *  - Handles `:has()`, container queries, and other CSS Level 4+ features
 *  - Foreign-object-based, so it inherits the browser's own rendering
 *
 * Default source is the feedback-hub host (same origin as the ingest API),
 * not a third-party CDN — keeps CSP simpler and removes unpkg supply-chain
 * risk. Override via `window.__feedbackHubScreenshotUrl` if needed.
 */

type ScreenshotOpts = {
  scale?: number;
  backgroundColor?: string;
  width?: number;
  height?: number;
  filter?: (el: Node) => boolean;
  // Force-embed these @font-face rules into the foreignObject clone.
  // modern-screenshot's auto font-embed skips faces it can't detect as
  // "used" — notably icon fonts applied only via ::before/::after
  // `content` (Bootstrap Icons, Font Awesome, Material Symbols …), which
  // then render as tofu squares. We collect + inline them ourselves.
  font?: { cssText?: string } | false;
  // Plus more — see https://github.com/qq15725/modern-screenshot
  timeout?: number;
};

type ModernScreenshot = {
  domToCanvas: (node: HTMLElement, options?: ScreenshotOpts) => Promise<HTMLCanvasElement>;
};

/** Pinned vendor path served by the inbox app (apps/inbox/public/vendor/). */
const VENDOR_PATH = "/vendor/modern-screenshot-4.7.0.js";
const ATTR = "data-feedback-hub-screenshot";

declare global {
  interface Window {
    modernScreenshot?: ModernScreenshot;
    __feedbackHubScreenshotUrl?: string;
  }
}

let pending: Promise<ModernScreenshot> | null = null;

/**
 * Resolve the script URL. Prefer the API endpoint host so CSP can allowlist
 * a single origin; fall back to production if endpoint is relative/missing.
 */
function defaultScriptSrc(endpoint?: string): string {
  const base = (endpoint ?? "https://feedback-hub.orosa.io").replace(/\/$/, "");
  try {
    return new URL(VENDOR_PATH, `${base}/`).toString();
  } catch {
    return `https://feedback-hub.orosa.io${VENDOR_PATH}`;
  }
}

function clearFailedScript() {
  document.querySelectorAll(`script[${ATTR}]`).forEach((el) => el.remove());
}

export function loadScreenshotLib(endpoint?: string): Promise<ModernScreenshot> {
  if (typeof window === "undefined")
    return Promise.reject(new Error("modern-screenshot: no window"));
  if (window.modernScreenshot) return Promise.resolve(window.modernScreenshot);
  if (pending) return pending;

  const src =
    window.__feedbackHubScreenshotUrl ?? defaultScriptSrc(endpoint);
  pending = new Promise<ModernScreenshot>((resolve, reject) => {
    // Never reuse a failed script tag — its load/error already fired, so
    // re-attaching listeners would hang forever.
    clearFailedScript();
    const s = document.createElement("script");
    s.src = src;
    s.async = true;
    s.setAttribute(ATTR, "");
    s.addEventListener("load", () => {
      if (window.modernScreenshot) resolve(window.modernScreenshot);
      else reject(new Error("modern-screenshot loaded but not on window"));
    });
    s.addEventListener("error", () =>
      reject(new Error(`Failed to load modern-screenshot from ${src}`)),
    );
    document.head.appendChild(s);
  }).then(
    (lib) => lib,
    (err) => {
      // Allow a later click to retry (network blip, vendor not yet deployed).
      pending = null;
      clearFailedScript();
      throw err;
    },
  );
  return pending;
}

/**
 * Collect every `@font-face` rule reachable from the document — including
 * ones in cross-origin stylesheets — and inline each font file as a base64
 * data URI, returning combined CSS to hand to modern-screenshot's
 * `font.cssText`.
 *
 * Why: modern-screenshot only auto-embeds fonts it detects as "used", and
 * its detection misses icon fonts that are applied purely through
 * `::before { content: "\fXXX" }` pseudo-elements — so Bootstrap Icons /
 * Font Awesome / Material Symbols capture as empty squares. Forcing the
 * faces in via `font.cssText` fixes it (verified against a faithful repro).
 *
 * Cross-origin sheets (Google Fonts et al., loaded via a plain `<link>`)
 * seal `cssRules`, and the library can't read them either — so pages using
 * a hosted webfont captured in a metrics-different fallback font, and text
 * sitting near a wrap boundary broke onto an extra line ("the screenshot
 * doesn't match my window" reports). The CSS itself is usually
 * CORS-fetchable even when cssRules is sealed (Google Fonts serves
 * `Access-Control-Allow-Origin: *`), so when cssRules throws we fetch the
 * sheet's text and parse the @font-face blocks out of it. Each face is
 * independently best-effort: any fetch/parse failure falls back to the
 * original rule (or skips the sheet) rather than aborting the capture.
 */
export async function collectFontFaceCss(): Promise<string> {
  if (typeof document === "undefined") return "";
  const out: string[] = [];
  for (const sheet of Array.from(document.styleSheets)) {
    let rules: CSSRuleList | undefined;
    try {
      rules = sheet.cssRules;
    } catch {
      // Cross-origin — cssRules is sealed, but the text usually isn't.
      if (sheet.href) {
        try {
          const resp = await fetch(sheet.href);
          if (resp.ok) {
            const text = await resp.text();
            for (const block of text.match(/@font-face\s*\{[^}]*\}/g) ?? []) {
              out.push(await inlineFontSrc(block, sheet.href));
            }
          }
        } catch {
          // opaque / CORS-blocked — leave to the library's best effort
        }
      }
      continue;
    }
    if (!rules) continue;
    for (const rule of Array.from(rules)) {
      if (
        typeof CSSFontFaceRule === "undefined" ||
        !(rule instanceof CSSFontFaceRule)
      )
        continue;
      out.push(await inlineFontSrc(rule.cssText, sheet.href));
    }
  }
  return out.join("\n");
}

/**
 * Inline the first `url()` of a serialized `@font-face` block as a base64
 * data URI (replacing the whole `src` list — same single-source behavior
 * the CSSOM path has always had). Returns the block unchanged on any
 * failure so a flaky font fetch can't break the capture.
 */
async function inlineFontSrc(
  cssText: string,
  baseHref: string | null,
): Promise<string> {
  const m = /url\((['"]?)([^'")]+)\1\)/.exec(cssText);
  if (!m) return cssText;
  try {
    const abs = new URL(m[2], baseHref ?? document.baseURI).href;
    const resp = await fetch(abs);
    if (!resp.ok) return cssText;
    const buf = await resp.arrayBuffer();
    const bytes = new Uint8Array(buf);
    let bin = "";
    for (let i = 0; i < bytes.length; i++)
      bin += String.fromCharCode(bytes[i]);
    const lc = abs.toLowerCase();
    const mime = lc.includes(".woff2")
      ? "font/woff2"
      : lc.includes(".woff")
        ? "font/woff"
        : lc.includes(".ttf")
          ? "font/ttf"
          : "font/otf";
    const dataUrl = `url(data:${mime};base64,${btoa(bin)})`;
    return cssText.replace(/src:\s*[^;}]+;?/, `src: ${dataUrl};`);
  } catch {
    return cssText; // keep the original rule on any failure
  }
}

/**
 * Best-effort detection of the page's effective backdrop color.
 *
 * Why: hardcoding `backgroundColor: "#ffffff"` on the capture canvas was
 * stamping white under the foreignObject clone, which leaked through on
 * any page with a dark/coloured background (Spacer Ledger reported it).
 * By spec the canvas bg comes from `html` if set, otherwise from `body`,
 * otherwise white — mirror that order.
 */
export function detectRootBackground(): string {
  if (typeof window === "undefined") return "#ffffff";
  const isOpaque = (c: string) =>
    !!c && c !== "transparent" && c !== "rgba(0, 0, 0, 0)";
  const htmlBg = getComputedStyle(document.documentElement).backgroundColor;
  if (isOpaque(htmlBg)) return htmlBg;
  const bodyBg = getComputedStyle(document.body).backgroundColor;
  if (isOpaque(bodyBg)) return bodyBg;
  return "#ffffff";
}

/**
 * NO LONGER CALLED as of the switch to capturing `document.documentElement`
 * (see FeedbackWidget `handleCaptureScreenshot`). When the capture root is
 * <html>, html-rooted theming selectors match natively in the clone and the
 * h-full/min-height collapse this works around can't happen. Retained for
 * reference in case the capture root is ever moved back to <body>.
 *
 * Patch the body so html-rooted theming (Tailwind `<html class="dark">`,
 * inline `color-scheme: dark`, `<html data-theme="dark">`) survives the
 * body-rooted clone modern-screenshot performs.
 *
 * Why: we pass `document.body` to `domToCanvas`, which serializes the body
 * subtree into a foreignObject SVG. The original `<html>` isn't an
 * ancestor of the cloned tree, so selectors like `html.dark .panel { ... }`
 * (what Tailwind compiles `dark:` variants down to) simply don't match
 * inside the clone — every dark-themed element falls back to default light
 * styles, which is why a dark page captures with bright white panels.
 *
 * Trick: mirror the html element's class, inline `color-scheme`, and
 * `data-theme` onto body for the duration of capture, so the same dark
 * descendant selectors match against body instead. The live page is
 * already painted via the html-rooted rules, so this duplication is
 * visually invisible during the brief capture window.
 *
 * Geometry caveat (load-bearing): copying html's *entire* className drags
 * along layout classes too — notably Tailwind's `h-full` (`height: 100%`).
 * When `<html class="h-full">` + `<body class="min-h-full …">` (a very
 * common Next.js shell), mirroring `h-full` onto the body makes its
 * `height: 100%` resolve against the viewport-tall html and COLLAPSE the
 * body from full content height down to one viewport. modern-screenshot
 * then captures only that top viewport-slice — which is both why captures
 * used to show the top of the page, and (once viewport-cropping landed)
 * why a scrolled capture came out as a thin horizontal strip. So we pin
 * the body's pre-mirror box height via `min-height` for the capture window
 * — a floor that defeats `height: 100%` without ever clipping content.
 * Verified against the ISG Web v2 home page (feedback 18afdcb8).
 *
 * Returns a restore fn — always call it (use `.finally`).
 */
export function mirrorRootThemingToBody(): () => void {
  if (typeof document === "undefined") return () => {};
  const html = document.documentElement;
  const body = document.body;
  if (!html || !body) return () => {};

  const prevClass = body.className;
  const prevColorScheme = body.style.colorScheme;
  const prevTheme = body.getAttribute("data-theme");
  const prevMinHeight = body.style.minHeight;
  // Measure the body's natural box height BEFORE mirroring any classes.
  const boxHeight = body.offsetHeight;

  if (html.className) {
    body.className = body.className
      ? `${body.className} ${html.className}`.trim()
      : html.className;
  }
  const cs = getComputedStyle(html).colorScheme;
  if (cs && cs !== "normal") body.style.colorScheme = cs;
  const theme = html.getAttribute("data-theme");
  if (theme !== null && body.getAttribute("data-theme") === null) {
    body.setAttribute("data-theme", theme);
  }
  // Pin height so a mirrored `h-full`/`height: 100%` can't collapse the
  // body mid-capture. min-height only raises the floor — never clips.
  if (boxHeight > 0) body.style.minHeight = `${boxHeight}px`;

  return () => {
    body.className = prevClass;
    if (prevColorScheme) body.style.colorScheme = prevColorScheme;
    else body.style.removeProperty("color-scheme");
    if (prevTheme === null) body.removeAttribute("data-theme");
    else body.setAttribute("data-theme", prevTheme);
    if (prevMinHeight) body.style.minHeight = prevMinHeight;
    else body.style.removeProperty("min-height");
  };
}

/**
 * Crop a full-page capture down to the region currently visible in the
 * viewport.
 *
 * Why: `domToCanvas(document.documentElement, …)` serializes the page from
 * its (0,0) origin, so the capture is always anchored to the TOP of the page
 * no matter how far the user has scrolled. Someone who scrolls to the bottom
 * of a long page and hits "Add screenshot" expects to see the bottom —
 * what they're looking at — not the masthead. We let the library render the
 * whole document (that's all it offers) and then slice out the viewport rect.
 *
 * The scale is derived empirically from the produced canvas vs the capture
 * basis (`canvas.width / rect.width`) rather than assumed, so it stays
 * correct regardless of the `scale` option or any devicePixelRatio the
 * library bakes in. `rootRect`'s origin comes from `getBoundingClientRect()`
 * on <html>, which folds the scroll offset into one measurement: `-rect.top`
 * is exactly how far the viewport has scrolled past the document's top edge.
 * Its width/height are the explicit content dims handed to `domToCanvas`
 * (NOT the <html> border box — see the h-full note at the call site).
 *
 * Falls back to the original canvas if the math degenerates (zero-sized
 * basis, no 2D context) — a top-anchored shot beats no shot.
 */
export function cropToViewport(
  canvas: HTMLCanvasElement,
  rootRect: { left: number; top: number; width: number; height: number },
  viewportW: number,
  viewportH: number,
): HTMLCanvasElement {
  if (typeof document === "undefined") return canvas;
  if (rootRect.width <= 0 || rootRect.height <= 0) return canvas;
  const sx = canvas.width / rootRect.width;
  const sy = canvas.height / rootRect.height;
  const cropX = Math.max(0, Math.round(-rootRect.left * sx));
  const cropY = Math.max(0, Math.round(-rootRect.top * sy));
  const cropW = Math.min(Math.round(viewportW * sx), canvas.width - cropX);
  const cropH = Math.min(Math.round(viewportH * sy), canvas.height - cropY);
  if (cropW <= 0 || cropH <= 0) return canvas;
  // Viewport already covers the whole capture — nothing to slice.
  if (cropX === 0 && cropY === 0 && cropW === canvas.width && cropH === canvas.height)
    return canvas;
  const out = document.createElement("canvas");
  out.width = cropW;
  out.height = cropH;
  const ctx = out.getContext("2d");
  if (!ctx) return canvas;
  ctx.drawImage(canvas, cropX, cropY, cropW, cropH, 0, 0, cropW, cropH);
  return out;
}

/**
 * Decode a Blob into something drawable on a canvas, cross-browser. Prefers
 * createImageBitmap (fast, off-main-thread decode) and falls back to an <img>
 * + object URL where it's missing or rejects the type. Caller must invoke
 * `cleanup()` once the draw is done.
 */
async function loadDrawableImage(src: Blob): Promise<{
  source: CanvasImageSource;
  width: number;
  height: number;
  cleanup: () => void;
}> {
  if (typeof createImageBitmap === "function") {
    try {
      const bmp = await createImageBitmap(src);
      return {
        source: bmp,
        width: bmp.width,
        height: bmp.height,
        cleanup: () => bmp.close(),
      };
    } catch {
      // Some browsers reject certain types here — fall through to the <img> path.
    }
  }
  const url = URL.createObjectURL(src);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error("image decode failed"));
      el.src = url;
    });
    return {
      source: img,
      width: img.naturalWidth || img.width,
      height: img.naturalHeight || img.height,
      cleanup: () => URL.revokeObjectURL(url),
    };
  } catch (err) {
    URL.revokeObjectURL(url);
    throw err;
  }
}

/**
 * Re-encode a user-provided image (file upload or clipboard paste) into a JPEG
 * blob via a canvas, downscaling so the longest edge is at most `maxDim` px
 * (matches the capture path's 1920 cap). JPEG keeps photos/mockups small —
 * re-encoding them to PNG would bloat them losslessly. Native canvas only, no
 * deps. The capture path keeps its own PNG output untouched; this is only for
 * the upload/paste sources whose bytes/format we don't control.
 */
export async function normalizeImageToJpeg(
  src: Blob,
  opts?: { maxDim?: number; quality?: number },
): Promise<{ blob: Blob; dataUrl: string }> {
  if (typeof document === "undefined") {
    throw new Error("normalizeImageToJpeg requires a DOM");
  }
  const maxDim = opts?.maxDim ?? 1920;
  const quality = opts?.quality ?? 0.9;

  const { source, width, height, cleanup } = await loadDrawableImage(src);
  try {
    if (width <= 0 || height <= 0) throw new Error("image has no dimensions");
    const scale = Math.min(1, maxDim / Math.max(width, height));
    const w = Math.max(1, Math.round(width * scale));
    const h = Math.max(1, Math.round(height * scale));

    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("2d context unavailable");
    // JPEG has no alpha — paint white first so a transparent PNG upload doesn't
    // composite onto black.
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(source, 0, 0, w, h);

    const blob: Blob = await new Promise((resolve, reject) => {
      canvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error("canvas.toBlob returned null"))),
        "image/jpeg",
        quality,
      );
    });
    const dataUrl = canvas.toDataURL("image/jpeg", quality);
    return { blob, dataUrl };
  } finally {
    cleanup();
  }
}

export type { ScreenshotOpts, ModernScreenshot };
